import React, {
  useState,
  useContext,
  useCallback,
  useEffect,
} from 'react';
import PropTypes from 'prop-types';
import format from 'string-template';
import {
  MenuItem,
  Checkbox,
  ListItemText,
} from '@mui/material';
import { WarningAmberRounded } from '@mui/icons-material';

import DialogRoundedModal, { ModalBody } from '../../../../../components/DialogRoundedModal';
import InputLabel from '../../../../../components/v2/InputLabel';
import TextField from '../../../../../components/v2/TextField';
import Select from '../../../../../components/v2/Select';
import { SaveButton } from '../../../../../components/Button/ActionButtons';
import LoadingOverlay from '../../../../components/LoadingOverlay';
import ManageClientContext from '../../../../context/ManageClientContext';
import useToast from '../../../../hooks/useToast';
import useCommunityCreation, { CommunityCreationStatus } from '../../../../hooks/useCommunityCreation';
import Community, { CommunityType } from '../../../../Model/Community';

import { SelectOption } from '../../styles';
import { TagsLockedNote } from './styles';
import texts from './texts.json';

const EMPTY_VALUE = '';

const CommunityFormModal = ({
  isOpen,
  onDidDismiss,
  coachId,
  community,
  onSaved,
}) => {
  const { clientTagsDocs } = useContext(ManageClientContext);
  const { showToast } = useToast();
  const { createCommunity, isCommunityNameTaken } = useCommunityCreation(coachId);

  const isEdit = !!community;

  const [name, setName] = useState(EMPTY_VALUE);
  const [selectedTags, setSelectedTags] = useState([]);
  const [nameError, setNameError] = useState(EMPTY_VALUE);
  const [isProcessing, setIsProcessing] = useState(false);
  const [isProductLinked, setIsProductLinked] = useState(false);
  // While the product-link check is in flight, keep the tag select disabled so a tag can't be picked
  // before we know whether the community is product-linked (tags aren't allowed on linked ones).
  const [isCheckingLink, setIsCheckingLink] = useState(false);

  useEffect(() => {
    if (!isOpen) {
      return undefined;
    }
    setName(community?.name || EMPTY_VALUE);
    setSelectedTags(community?.customTags || []);
    setNameError(EMPTY_VALUE);
    setIsProductLinked(false);
    if (!community) {
      setIsCheckingLink(false);
      return undefined;
    }
    let isActive = true;
    setIsCheckingLink(true);
    community.hasLinkedProducts()
      .then((linked) => {
        if (isActive) {
          setIsProductLinked(linked);
        }
      })
      .finally(() => {
        if (isActive) {
          setIsCheckingLink(false);
        }
      });
    return () => {
      isActive = false;
    };
  }, [isOpen, community]);

  const handleNameChange = useCallback((event) => {
    setName(event.target.value);
    setNameError(EMPTY_VALUE);
  }, []);

  const handleSave = useCallback(async () => {
    const trimmedName = name.trim();
    if (!trimmedName) {
      setNameError(texts.nameRequiredToast);
      return;
    }

    try {
      setIsProcessing(true);

      if (isEdit) {
        if (isCommunityNameTaken(trimmedName, community.id)) {
          setNameError(texts.nameTakenToast);
          return;
        }
        await community.updateFields({
          name: trimmedName,
          customTags: selectedTags,
          type: selectedTags.length ? CommunityType.TAG : CommunityType.CUSTOM,
          updatedAt: new Date(),
        });
        showToast(texts.updatedToast, { success: true });
        onSaved?.(community.id);
        onDidDismiss();
        return;
      }

      const result = await createCommunity({ name: trimmedName, customTags: selectedTags });
      if (result.status === CommunityCreationStatus.NAME_TAKEN) {
        setNameError(texts.nameTakenToast);
        return;
      }
      if (result.status === CommunityCreationStatus.ERROR) {
        showToast(format(texts.failedToast, { error: result.message }), { error: true });
        return;
      }
      showToast(texts.createdToast, { success: true });
      onSaved?.(result.id);
      onDidDismiss();
    } catch (error) {
      showToast(format(texts.failedToast, { error: error.message }), { error: true });
    } finally {
      setIsProcessing(false);
    }
  }, [
    name, community, isEdit, selectedTags, createCommunity, isCommunityNameTaken,
    showToast, onSaved, onDidDismiss,
  ]);

  return (
    <>
      <DialogRoundedModal
        open={isOpen}
        onClose={onDidDismiss}
        title={isEdit ? texts.titleEdit : texts.titleNew}
        maxWidth="sm"
        fullWidth
        actionButtons={(
          <SaveButton
            variant="contained"
            onClick={handleSave}
            disabled={!name.trim() || isProcessing}
          >
            {isEdit ? texts.saveButton : texts.createButton}
          </SaveButton>
        )}
      >
        <ModalBody>
          <InputLabel>{texts.nameLabel}</InputLabel>
          <TextField
            value={name}
            onChange={handleNameChange}
            placeholder={texts.namePlaceholder}
            error={!!nameError}
            helperText={nameError}
          />
          <SelectOption>
            <InputLabel>{texts.tagsLabel}</InputLabel>
            <Select
              multiple
              displayEmpty
              disabled={isProductLinked || isCheckingLink}
              value={selectedTags}
              onChange={(event) => setSelectedTags(event.target.value)}
              renderValue={(selected) => (selected.length ? selected.join(', ') : '')}
            >
              {clientTagsDocs.map((doc) => (
                <MenuItem key={doc.id} value={doc.tag}>
                  <Checkbox checked={selectedTags.includes(doc.tag)} />
                  <ListItemText primary={doc.tag} />
                </MenuItem>
              ))}
            </Select>
            {isProductLinked && (
              <TagsLockedNote>
                <WarningAmberRounded />
                {texts.tagsLockedNote}
              </TagsLockedNote>
            )}
          </SelectOption>
        </ModalBody>
      </DialogRoundedModal>
      <LoadingOverlay isLoading={isProcessing} />
    </>
  );
};

CommunityFormModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onDidDismiss: PropTypes.func.isRequired,
  coachId: PropTypes.string.isRequired,
  community: PropTypes.instanceOf(Community),
  onSaved: PropTypes.func,
};

CommunityFormModal.defaultProps = {
  community: null,
  onSaved: null,
};

export default CommunityFormModal;
